import { useEffect, useRef } from 'react';
import { trackEvent } from '../analytics';
import { tierChangeDirection } from '../pricing/subscription';
import { consumeUpsellSource, consumePortalTier, UPSELL_FLOWS } from '../upsell/attribution';

// Closes the #350 funnel: everything up to `subscribe_checkout_started` happens
// on our pages, but the conversion itself happens on Stripe. Mounted on the page
// Stripe returns to (/account, the checkout success_url), this watches the
// users/{uid} snapshot for the state the webhook writes and emits
// `subscription_activated` / `tier_upgraded` / `tier_downgraded`.
//
// Two paths, because the webhook and the redirect race each other:
//
//  * TRANSITION — the page loads before the webhook lands, so the first snapshot
//    is unsubscribed and a later one is active. This is the common case, and it
//    also covers a reactivation from the billing portal.
//  * FIRST-SNAPSHOT + attribution — the webhook won the race, so the very first
//    snapshot is already active and there is no transition to observe. A source
//    in sessionStorage means THIS session started a checkout, which is the only
//    evidence that this active subscription is new. consumeUpsellSource is
//    destructive, so a refresh doesn't re-count the conversion.
//
// Deliberately does NOT fire on a plain page load with no remembered checkout:
// a subscriber visiting /account is not an activation, and counting it would
// make the conversion number useless.
//
// Tier moves have the same two-path problem, plus a third: a change made in the
// Stripe billing portal returns through `return_url` as a FULL page load, so
// there is no in-memory snapshot to diff and the webhook has usually already
// written the new tier. PlanBillingSection stashes the before-tier on the way
// out (rememberPortalTier) and the first snapshot is compared against it.
const PAID_STATUSES = ['active', 'past_due'];

const isPaid = (subscription) => Boolean(subscription)
  && PAID_STATUSES.includes(subscription.status);

export default function useSubscriptionFunnelEvents({
  subscription,
  entitlements,
  loading,
} = {}) {
  // What we last observed. `undefined` = nothing observed yet this session,
  // which is what distinguishes "first snapshot" from a real transition.
  const seenRef = useRef({ paid: undefined, tier: undefined });

  const paid = isPaid(subscription);
  const tier = (entitlements && entitlements.tier) || null;
  const planId = (subscription && subscription.tier) || tier;

  useEffect(() => {
    // Nothing to compare against while the snapshot is still in flight —
    // recording the loading placeholder as "observed" would turn the first real
    // snapshot into a spurious transition.
    if (loading || !entitlements) return;

    const seen = seenRef.current;
    const first = seen.paid === undefined;

    if (paid && (first || !seen.paid)) {
      // A first snapshot only counts as an activation when this session sent
      // them to a SUBSCRIBE checkout; a transition always counts. Reading the
      // subscribe key specifically — a stashed top-up click is not evidence of
      // a new subscription.
      const source = consumeUpsellSource(UPSELL_FLOWS.SUBSCRIBE);
      if (!first || source) {
        trackEvent('subscription_activated', {
          plan_id: planId,
          tier,
          source: source || 'unknown',
        });
      }
    }

    // The tier the account held when it left for the billing portal, if this
    // load is the return from one. Always consumed on the first snapshot so a
    // stale value cannot attach itself to a later transition.
    const portalTier = first ? consumePortalTier() : null;
    // A portal plan change returns as a full page load, so there is usually no
    // transition to observe: the first snapshot already carries the new tier.
    // Diffing it against the stashed before-tier is the only way that move —
    // the highest-value conversion an existing subscriber can make — is
    // reported at all. A live transition still takes the `seen.tier` path, and
    // the consume above keeps the two from double-counting the same move.
    const fromTier = first ? portalTier : seen.tier;
    if (fromTier && tier) {
      const direction = tierChangeDirection(fromTier, tier);
      if (direction) {
        trackEvent(direction, { from_tier: fromTier, to_tier: tier });
      }
    }

    seenRef.current = { paid, tier };
  }, [loading, entitlements, paid, tier, planId]);
}
